import { trackEvent, AnalyticsEvents } from '../../analytics';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Drawer,
  Button,
  Stack,
  Group,
  Loader,
  Text,
  Paper,
  Avatar,
  Progress,
  Badge,
  ActionIcon,
  Collapse,
  Box,
  Flex,
  ThemeIcon,
  Alert,
  Switch,
  UnstyledButton,
} from '@mantine/core';
import {
  IconArrowRight,
  IconArrowBackUp,
  IconCircleOff,
  IconDeviceFloppy,
  IconLock,
  IconCheck,
  IconChevronDown,
  IconUser,
  IconX,
  IconLayoutGrid,
  IconAlertCircle,
} from '@tabler/icons-react';
import { ReviewMapPanel } from './ReviewMapPanel';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  QuestionField,
  CANNOT_ASSESS,
  isScaleScored,
  isAnswered,
  resolveScaleLabels,
} from './QuestionField';
import { useReviewQuestionModel } from '../../hooks/useReviewQuestionModel';
import {
  useGetPerformanceReviewFormsId,
  useGetPerformanceReviewQuestionnairesId,
  useGetPerformanceReviewRoundsId,
  useGetPerformanceReviewRoundsRoundIdSubjectsUserIdSelf,
  usePatchPerformanceReviewFormsIdDraft,
  usePostPerformanceReviewFormsIdSubmit,
  getGetPerformanceReviewRoundsRoundIdFormsMineQueryKey,
  getGetPerformanceReviewFormsIdQueryKey,
} from '../../api/generated/performance-review/performance-review';
import { PerformanceReviewForm } from '../../api/generated/model/performanceReviewForm';
import { PerformanceReviewRound } from '../../api/generated/model/performanceReviewRound';
import { PerformanceReviewQuestionnaire } from '../../api/generated/model/performanceReviewQuestionnaire';
import { PerformanceReviewQuestion } from '../../api/generated/model/performanceReviewQuestion';
import { useDevice } from '../../hooks/useDevice';
import { User } from '../../types';
import { showToast } from '../../utils/toast';
import { api, getErrorMessage } from '../../utils/api';

interface Props {
  formId: string;
  opened: boolean;
  onClose: () => void;
}

type AutosaveStatus = 'idle' | 'saving' | 'saved' | 'error';

const initialsOf = (name: string): string => {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() || '').join('');
};

// Inline replacement for Mantine's <ScrollArea>. We just render a plain
// div so the surrounding Drawer.body becomes the native scroll container,
// which makes `position: sticky` work for the progress bar.
const NativeScrollArea: React.FC<{ children: React.ReactNode }> = ({ children }) => <>{children}</>;

interface SelfAnswer {
  questionId?: string | null;
  value?: unknown;
}

const renderSelfValue = (
  q: PerformanceReviewQuestion,
  value: unknown,
  notAnsweredLabel: string,
  cannotAssessLabel: string,
  scaleLabel?: string,
): React.ReactNode => {
  if (value === undefined || value === '') {
    return (
      <Text size="sm" c="dimmed" fs="italic">
        {notAnsweredLabel}
      </Text>
    );
  }
  if (q.type === 'scale') {
    if (value === CANNOT_ASSESS || value === null) {
      return (
        <Badge variant="light" color="gray" size="sm">
          {cannotAssessLabel}
        </Badge>
      );
    }
    if (isScaleScored(value)) {
      // Read-only mirror of the editable widget: the score number + its label,
      // not stars (the widget dropped stars for labelled points).
      return (
        <Group gap={8} align="center" wrap="nowrap">
          <Badge variant="light" color="teal" size="sm" radius="sm">
            {value as number}
          </Badge>
          {scaleLabel && <Text size="sm">{scaleLabel}</Text>}
        </Group>
      );
    }
    return (
      <Text size="sm" c="dimmed">
        —
      </Text>
    );
  }
  if (value === null) {
    return (
      <Text size="sm" c="dimmed" fs="italic">
        {notAnsweredLabel}
      </Text>
    );
  }
  if (q.type === 'multi-select' && Array.isArray(value)) {
    return (
      <Group gap={4} wrap="wrap">
        {value.map((v, i) => (
          <Badge key={`${v}-${i}`} variant="light" color="blue" size="sm">
            {String(v)}
          </Badge>
        ))}
      </Group>
    );
  }
  if (typeof value === 'string') {
    return (
      <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
        {value}
      </Text>
    );
  }
  return <Text size="sm">{String(value)}</Text>;
};

export const PerformanceReviewFormModal: React.FC<Props> = ({ formId, opened, onClose }) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const deviceType = useDevice();
  const isMobile = deviceType === 'mobile';

  const { data: form } = useGetPerformanceReviewFormsId(formId, { query: { enabled: opened } });
  const f = form as unknown as PerformanceReviewForm | undefined;
  const { data: round } = useGetPerformanceReviewRoundsId(f?.round || '', {
    query: { enabled: !!f?.round },
  });
  const r = round as unknown as PerformanceReviewRound | undefined;
  const { data: questionnaire } = useGetPerformanceReviewQuestionnairesId(r?.questionnaire || '', {
    query: { enabled: !!r?.questionnaire },
  });
  const q = questionnaire as unknown as PerformanceReviewQuestionnaire | undefined;
  const saveDraft = usePatchPerformanceReviewFormsIdDraft();
  const submit = usePostPerformanceReviewFormsIdSubmit();
  const reopen = useMutation({
    mutationFn: async () => api(`/performance-review/forms/${formId}/reopen`, { method: 'POST' }),
  });

  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const [deanonMap, setDeanonMap] = useState<Record<string, boolean>>({});
  const [wantsGradePromotion, setWantsGradePromotion] = useState(false);
  const [autosaveStatus, setAutosaveStatus] = useState<AutosaveStatus>('idle');
  const [isSelfPanelOpen, setIsSelfPanelOpen] = useState(true);
  // Left companion panel: the subject's class map with their acquired competencies
  // marked. Opened by clicking a grade badge; `mapPanelGrade` just labels which
  // grade was clicked (the panel shows the whole map).
  const [isMapPanelOpen, setIsMapPanelOpen] = useState(false);
  const [focusGradeId, setFocusGradeId] = useState<string | undefined>();
  const openMapPanel = (gradeId?: string) => {
    setFocusGradeId(gradeId);
    setIsMapPanelOpen(true);
  };
  const lastSavedJsonRef = useRef<string>('');
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (f?.answers) {
      const map: Record<string, unknown> = {};
      const dmap: Record<string, boolean> = {};
      for (const a of f.answers) {
        map[a.questionId ?? ''] = a.value;
        if (a.deanonymized) dmap[a.questionId ?? ''] = true;
      }
      setAnswers(map);
      setDeanonMap(dmap);
      const promotion = (f as { wantsGradePromotion?: boolean }).wantsGradePromotion === true;
      setWantsGradePromotion(promotion);
      lastSavedJsonRef.current = JSON.stringify({ a: map, d: dmap, p: promotion });
      setAutosaveStatus('idle');
    }
    // Re-hydrate when status flips to submitted (after our own submit triggers a refetch),
    // so the read-only view reflects the persisted server snapshot.
  }, [formId, f?._id, f?.status]);

  const subject = useMemo(() => {
    if (!f) return undefined;
    return f.subject && typeof f.subject === 'object' ? (f.subject as User) : undefined;
  }, [f]);
  const subjectName = subject?.name || '';
  const subjectId = subject?._id || (typeof f?.subject === 'string' ? f.subject : '');

  const isSelf = f?.type === 'self';
  const isManager = f?.type === 'manager';
  const subjectProf = subject?.profession as unknown as { name?: string } | string | undefined;
  const subjectProfName = typeof subjectProf === 'string' ? subjectProf : subjectProf?.name;

  // Peer/manager questions are snapshotted on the form itself; if empty the model
  // surfaces a configuration error rather than guessing from another source.
  const formQuestions = (f as { questions?: PerformanceReviewQuestion[] } | undefined)?.questions;
  const readOnly = f?.status === 'submitted';

  // Visible question list, block sections, required-comment set and progress —
  // derived in one place (see useReviewQuestionModel).
  const {
    questions,
    sections,
    commentRequiredIds,
    filledCount,
    totalCount,
    requiredRemaining,
    feedbackQuestionnaireEmpty,
  } = useReviewQuestionModel({
    isSelf,
    readOnly,
    questionnaire: q,
    formQuestions,
    formAnswers: f?.answers,
    answers,
    t,
  });

  const hasAnonymous = questions.some((qq) => qq.anonymous === true);

  // Subject's submitted self-form (visible to peer/manager reviewers and admins).
  const { data: subjectSelf } = useGetPerformanceReviewRoundsRoundIdSubjectsUserIdSelf(
    f?.round || '',
    subjectId,
    { query: { enabled: !!f?.round && !!subjectId && !isSelf, retry: false } },
  );
  const subjectSelfForm = subjectSelf as unknown as { answers?: SelfAnswer[] } | undefined;
  const subjectSelfAnswers = useMemo(() => {
    const map: Record<string, unknown> = {};
    for (const a of subjectSelfForm?.answers || []) {
      if (a.questionId != null) map[a.questionId] = a.value;
    }
    return map;
  }, [subjectSelfForm]);
  const selfQuestions = q?.selfForm?.questions || [];

  const persistDraft = async (
    snapshot: Record<string, unknown>,
    dsnapshot: Record<string, boolean>,
  ): Promise<boolean> => {
    setAutosaveStatus('saving');
    try {
      await saveDraft.mutateAsync({
        id: formId,
        data: {
          answers: Object.entries(snapshot).map(([questionId, value]) => {
            const item: { questionId: string; value: unknown; deanonymized?: boolean } = {
              questionId,
              value,
            };
            if (dsnapshot[questionId]) item.deanonymized = true;
            return item as never;
          }),
          ...(isSelf ? { wantsGradePromotion } : {}),
        } as never,
      });
      lastSavedJsonRef.current = JSON.stringify({
        a: snapshot,
        d: dsnapshot,
        p: isSelf ? wantsGradePromotion : false,
      });
      setAutosaveStatus('saved');
      return true;
    } catch {
      setAutosaveStatus('error');
      return false;
    }
  };

  // Explicit "save draft" — autosave still runs, but a manual control gives the
  // reviewer certainty their answers are stored (and surfaces save failures).
  const handleSaveDraft = async () => {
    const ok = await persistDraft(answers, deanonMap);
    showToast(
      ok
        ? {
            title: t('perfReview.draftSavedTitle'),
            message: t('perfReview.draftSavedMessage'),
            color: 'teal',
          }
        : {
            title: t('perfReview.draftSaveFailedTitle'),
            message: t('perfReview.draftSaveFailedMessage'),
            color: 'red',
          },
    );
  };

  // Autosave: 1s debounce after edits.
  useEffect(() => {
    if (!opened || readOnly || !f) return;
    const json = JSON.stringify({
      a: answers,
      d: deanonMap,
      p: isSelf ? wantsGradePromotion : false,
    });
    if (json === lastSavedJsonRef.current) return;
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(() => {
      persistDraft(answers, deanonMap);
    }, 1000);
    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers, deanonMap, wantsGradePromotion, opened, readOnly, f?._id, isSelf]);

  const flushIfDirty = () => {
    const json = JSON.stringify({
      a: answers,
      d: deanonMap,
      p: isSelf ? wantsGradePromotion : false,
    });
    if (json === lastSavedJsonRef.current) return;
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    persistDraft(answers, deanonMap);
  };

  useEffect(() => {
    if (!opened) return;
    const onVis = () => {
      if (document.visibilityState === 'hidden') flushIfDirty();
    };
    window.addEventListener('beforeunload', flushIfDirty);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('beforeunload', flushIfDirty);
      document.removeEventListener('visibilitychange', onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, answers]);

  const handleClose = () => {
    flushIfDirty();
    onClose();
  };

  const handleSubmit = async () => {
    try {
      await persistDraft(answers, deanonMap);
      await submit.mutateAsync({ id: formId });
      trackEvent(AnalyticsEvents.REVIEW_SUBMITTED, { type: f?.type });
      queryClient.invalidateQueries({
        queryKey: getGetPerformanceReviewFormsIdQueryKey(formId),
      });
      if (f?.round) {
        queryClient.invalidateQueries({
          queryKey: getGetPerformanceReviewRoundsRoundIdFormsMineQueryKey(f.round),
        });
      }
      showToast({
        title: t('perfReview.submittedTitle'),
        message: t('perfReview.feedbackThanks'),
        color: 'teal',
      });
      onClose();
    } catch (e) {
      showToast({
        title: t('perfReview.submitFailed'),
        message: getErrorMessage(e, ''),
        color: 'red',
      });
    }
  };

  // A submitted form can be reopened only by its respondent (backend enforces)
  // and only while its collection stage is still open, so the extra optional
  // comments can be added without losing prior answers.
  const canReopen =
    readOnly && (isSelf ? r?.stage === 'collect-essay' : r?.stage === 'collect-feedback');

  const handleReopen = async () => {
    try {
      await reopen.mutateAsync();
      queryClient.invalidateQueries({
        queryKey: getGetPerformanceReviewFormsIdQueryKey(formId),
      });
      if (f?.round) {
        queryClient.invalidateQueries({
          queryKey: getGetPerformanceReviewRoundsRoundIdFormsMineQueryKey(f.round),
        });
      }
      showToast({
        title: t('perfReview.reopenedTitle'),
        message: t('perfReview.reopenedMessage'),
        color: 'blue',
      });
    } catch (e) {
      showToast({
        title: t('perfReview.reopenFailed'),
        message: getErrorMessage(e, ''),
        color: 'red',
      });
    }
  };

  // Bulk opt-out: a reviewer who can't assess this person at all marks every
  // scale question "cannot assess" in one click instead of tapping each block.
  // Comments stay optional (the block rule only requires them once a scale is
  // actually scored), so the form becomes submittable right away. Reversible —
  // individual scales can still be changed afterwards.
  const hasScaleQuestions = questions.some((qq) => qq.type === 'scale');
  const markAllCannotAssess = () => {
    setAnswers((prev) => {
      const next = { ...prev };
      for (const qq of questions) {
        if (qq.type === 'scale' && qq.id) next[qq.id] = CANNOT_ASSESS;
      }
      return next;
    });
  };

  const titleEyebrow = (() => {
    if (r?.title) return r.title.toUpperCase();
    return t('perfReview.title').toUpperCase();
  })();
  const titleMain = isSelf
    ? t('perfReview.selfReview')
    : isManager
      ? t('perfReview.managerReview')
      : t('perfReview.peerReview');

  if (!f || !q) {
    return (
      <Drawer
        opened={opened}
        onClose={onClose}
        withCloseButton={false}
        position={isMobile ? 'bottom' : 'right'}
        size={isMobile ? '100%' : 760}
      >
        <Flex justify="center" py="xl">
          <Loader size="sm" />
        </Flex>
      </Drawer>
    );
  }

  const teamNames = (subject?.teams || [])
    .map((tm) => (typeof tm === 'object' ? tm.name : tm))
    .filter(Boolean);
  const subjectBadgeStyles = {
    root: {
      height: 24,
      paddingInline: 10,
      lineHeight: '22px',
      display: 'inline-flex',
      alignItems: 'center',
      fontSize: 11,
      letterSpacing: '0.08em',
      textTransform: 'uppercase' as const,
      fontWeight: 700,
    },
    label: {
      display: 'inline-flex',
      alignItems: 'center',
      lineHeight: '22px',
    },
    section: {
      display: 'inline-flex',
      alignItems: 'center',
      marginInlineStart: 5,
    },
  };

  // Compact autosave indicator on top-right of progress bar.
  const autosaveIndicator = (() => {
    if (readOnly) return null;
    if (autosaveStatus === 'saving') {
      return (
        <Group gap={6} wrap="nowrap">
          <Loader size={11} color="gray" />
          <Text size="xs" c="dimmed" fw={500}>
            {t('perfReview.autosaveSaving')}
          </Text>
        </Group>
      );
    }
    if (autosaveStatus === 'saved') {
      return (
        <Group gap={6} wrap="nowrap" c="teal">
          <IconCheck size={12} />
          <Text size="xs" fw={500} c="teal">
            {t('perfReview.autosaveSaved')}
          </Text>
        </Group>
      );
    }
    if (autosaveStatus === 'error') {
      return (
        <Text size="xs" c="red" fw={500}>
          {t('perfReview.autosaveError')}
        </Text>
      );
    }
    return (
      <Text size="xs" c="dimmed" style={{ opacity: 0.7 }}>
        {t('perfReview.autosaveSaved')}
      </Text>
    );
  })();

  return (
    <Drawer
      opened={opened}
      onClose={handleClose}
      withCloseButton={false}
      position={isMobile ? 'bottom' : 'right'}
      size={isMobile ? '100%' : isMapPanelOpen ? 1240 : 760}
      padding={0}
      scrollAreaComponent={NativeScrollArea}
      styles={{
        content: { display: 'flex', flexDirection: 'column' },
        body: {
          flex: 1,
          display: 'flex',
          flexDirection: !isMobile && isMapPanelOpen ? 'row' : 'column',
          minHeight: 0,
          padding: 0,
          overflow: 'hidden',
        },
      }}
    >
      {/* Left companion: subject's competency map, opened from a grade badge.
          Widens this same drawer instead of stacking a second one (which broke
          the form's scroll lock). */}
      {isMapPanelOpen && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            minWidth: 0,
            flexShrink: 0,
            ...(isMobile
              ? {
                  width: '100%',
                  maxHeight: '45vh',
                  borderBottom: '1px solid var(--mantine-color-default-border)',
                }
              : { width: 560, borderRight: '1px solid var(--mantine-color-default-border)' }),
          }}
        >
          <ReviewMapPanel
            onClose={() => setIsMapPanelOpen(false)}
            mapId={subject?.competencyMapId}
            competencies={subject?.competencies}
            currentGradeId={subject?.grade?._id}
            focusGradeId={focusGradeId}
            subjectName={subjectName}
          />
        </div>
      )}
      {/* Form column */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0 }}>
        {/* Custom header */}
        <Flex
          align="center"
          justify="space-between"
          px={isMobile ? 'md' : 'xl'}
          py="md"
          style={{
            flexShrink: 0,
            borderBottom: '1px solid var(--mantine-color-default-border)',
            background: 'var(--mantine-color-body)',
          }}
        >
          <Box style={{ minWidth: 0, flex: 1 }}>
            <Text
              size="xs"
              fw={600}
              c="dimmed"
              mb={4}
              style={{ letterSpacing: '0.14em', textTransform: 'uppercase' }}
              truncate
            >
              {titleEyebrow}
            </Text>
            <Text size="md" fw={600} style={{ letterSpacing: '-0.005em' }} truncate>
              {titleMain}
              {!isSelf && subjectName && (
                <>
                  {' '}
                  {t('common.for', 'для')}{' '}
                  <Text component="span" c="teal" fw={600} inherit>
                    {subjectName}
                  </Text>
                </>
              )}
            </Text>
          </Box>
          <ActionIcon
            variant="default"
            size={36}
            radius="md"
            onClick={handleClose}
            aria-label="Close"
          >
            <IconX size={16} />
          </ActionIcon>
        </Flex>

        {/* Progress strip — non-scrollable section between header and body */}
        {!readOnly && totalCount > 0 && (
          <Box
            px={isMobile ? 'md' : 'xl'}
            py="sm"
            style={{
              flexShrink: 0,
              background: 'var(--mantine-color-body)',
              borderBottom: '1px solid var(--mantine-color-default-border)',
            }}
          >
            <Flex justify="space-between" align="center" gap="xs" wrap="wrap" mb={8}>
              <Group gap="xs" wrap="nowrap" align="center">
                <Text size="sm" fw={500}>
                  {t('perfReview.answeredOf', 'Отвечено')}{' '}
                  <Text component="span" inherit fw={700}>
                    {filledCount}
                  </Text>{' '}
                  {t('common.from', 'из')}{' '}
                  <Text component="span" inherit>
                    {totalCount}
                  </Text>
                </Text>
                {requiredRemaining > 0 ? (
                  <Badge color="orange" variant="light" size="sm" radius="xl">
                    {t('perfReview.requiredRemainingShort', 'Осталось обязательных')} ·{' '}
                    {requiredRemaining}
                  </Badge>
                ) : (
                  <Badge
                    color="teal"
                    variant="light"
                    size="sm"
                    radius="xl"
                    leftSection={<IconCheck size={11} />}
                  >
                    {t('perfReview.allRequiredDone', 'Все обязательные сданы')}
                  </Badge>
                )}
              </Group>
              <Group gap="xs" wrap="nowrap" align="center">
                {!isSelf && hasScaleQuestions && (
                  <Button
                    variant="subtle"
                    color="gray"
                    size="compact-xs"
                    leftSection={<IconCircleOff size={13} />}
                    onClick={markAllCannotAssess}
                  >
                    {t('perfReview.cannotAssessAll', 'Не могу оценить всё')}
                  </Button>
                )}
                {/* Fixed-width slot: the indicator swaps between idle/saving/saved
                    on every keystroke and each state has a different width. Pin the
                    footprint so it never reflows the header (no more field twitch). */}
                <Box
                  style={{
                    width: 116,
                    flexShrink: 0,
                    display: 'flex',
                    justifyContent: 'flex-end',
                  }}
                >
                  {autosaveIndicator}
                </Box>
              </Group>
            </Flex>
            <Progress
              value={totalCount ? (filledCount / totalCount) * 100 : 0}
              color="teal"
              size="xs"
              radius="xl"
            />
          </Box>
        )}

        {/* Scrollable body */}
        <Box
          px={isMobile ? 'md' : 'xl'}
          py="lg"
          style={{
            flex: 1,
            overflowY: 'auto',
            minHeight: 0,
          }}
        >
          <Stack gap="md">
            {isSelf && (
              <Paper withBorder radius="lg" p="md">
                <Switch
                  color="teal"
                  checked={wantsGradePromotion}
                  disabled={readOnly}
                  onChange={(event) => setWantsGradePromotion(event.currentTarget.checked)}
                  label={t('perfReview.wantsGradePromotionLabel')}
                  description={t('perfReview.wantsGradePromotionDescription')}
                />
              </Paper>
            )}

            {!isSelf && subject && (
              <Paper withBorder radius="lg" p="lg">
                <Group wrap="nowrap" align="center">
                  <Avatar src={subject.avatar} radius="xl" size={isMobile ? 48 : 56} color="teal">
                    {initialsOf(subject.name)}
                  </Avatar>
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Text
                      fw={600}
                      size={isMobile ? 'md' : 'lg'}
                      style={{ letterSpacing: '-0.005em' }}
                      truncate
                    >
                      {subject.name}
                    </Text>
                    <Text size="sm" c="dimmed" mt={4} truncate>
                      {[subject.position, subjectProfName, teamNames[0]]
                        .filter(Boolean)
                        .join(' · ')}
                    </Text>
                    <Group gap={6} mt={10} wrap="wrap" align="center">
                      {/* Клик по грейду открывает слева карту компетенций ревьюемого
                        с его отметками — свериться с требованиями грейда. */}
                      {subject.grade?.title && (
                        <UnstyledButton onClick={() => openMapPanel(subject.grade?._id)}>
                          <Badge
                            color="teal"
                            variant="light"
                            size="sm"
                            radius="xl"
                            rightSection={<IconLayoutGrid size={11} />}
                            styles={subjectBadgeStyles}
                            style={{ cursor: 'pointer' }}
                          >
                            {t('perfReview.currentGrade')}: {subject.grade.title}
                          </Badge>
                        </UnstyledButton>
                      )}
                      {subject.promotionRequested && subject.nextGrade?.title && (
                        <UnstyledButton onClick={() => openMapPanel(subject.nextGrade?._id)}>
                          <Badge
                            color="violet"
                            variant="light"
                            size="sm"
                            radius="xl"
                            rightSection={<IconLayoutGrid size={11} />}
                            styles={subjectBadgeStyles}
                            style={{ cursor: 'pointer' }}
                          >
                            {t('perfReview.promotionRequestedTo')}: {subject.nextGrade.title}
                          </Badge>
                        </UnstyledButton>
                      )}
                      {subjectProfName && (
                        <Badge variant="default" size="sm" radius="xl" styles={subjectBadgeStyles}>
                          {subjectProfName}
                        </Badge>
                      )}
                      {teamNames.slice(0, 2).map((tn) => (
                        <Badge
                          key={tn}
                          variant="default"
                          size="sm"
                          radius="xl"
                          styles={subjectBadgeStyles}
                        >
                          {tn}
                        </Badge>
                      ))}
                    </Group>
                  </Box>
                </Group>
              </Paper>
            )}

            {!isSelf && subjectSelfForm && selfQuestions.length > 0 && (
              <Paper withBorder radius="lg" p={0} style={{ overflow: 'hidden' }}>
                <Box
                  component="button"
                  type="button"
                  onClick={() => setIsSelfPanelOpen((v) => !v)}
                  style={{
                    width: '100%',
                    padding: '14px 20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    color: 'inherit',
                    textAlign: 'left',
                  }}
                >
                  <Group gap="md" wrap="nowrap" style={{ minWidth: 0 }}>
                    <ThemeIcon size={32} radius="md" variant="default" color="gray">
                      <IconUser size={16} />
                    </ThemeIcon>
                    <Box style={{ minWidth: 0 }}>
                      <Text size="sm" fw={600} truncate>
                        {t('perfReview.subjectWroteAboutSelf', {
                          name: subject?.name || subjectName || '—',
                        })}
                      </Text>
                      <Text size="xs" c="dimmed" mt={2} truncate>
                        {t('perfReview.selfContextSubtitle', 'Контекст из self-эссе участника')}
                      </Text>
                    </Box>
                  </Group>
                  <IconChevronDown
                    size={14}
                    style={{
                      color: 'var(--mantine-color-dimmed)',
                      transform: isSelfPanelOpen ? 'rotate(180deg)' : 'none',
                      transition: 'transform 120ms',
                      flexShrink: 0,
                    }}
                  />
                </Box>
                <Collapse in={isSelfPanelOpen}>
                  <Stack gap="md" p="lg" pt={0}>
                    {selfQuestions.map((sq) => (
                      <Box
                        key={sq.id}
                        pl="md"
                        style={{
                          borderLeft: '2px solid var(--mantine-color-default-border)',
                        }}
                      >
                        <Text
                          size="xs"
                          fw={600}
                          c="dimmed"
                          mb={4}
                          style={{ letterSpacing: '0.1em', textTransform: 'uppercase' }}
                        >
                          {sq.text}
                        </Text>
                        {(() => {
                          const val = subjectSelfAnswers[sq.id ?? ''];
                          const defs = t('perfReview.expectationScale', { returnObjects: true });
                          const labels = resolveScaleLabels(
                            sq,
                            Array.isArray(defs) ? (defs as string[]) : undefined,
                          );
                          const scaleLabel = isScaleScored(val)
                            ? labels?.[(val as number) - 1]
                            : undefined;
                          return renderSelfValue(
                            sq,
                            val,
                            t('perfReview.noAnswer'),
                            t('perfReview.cannotAssess'),
                            scaleLabel,
                          );
                        })()}
                      </Box>
                    ))}
                  </Stack>
                </Collapse>
              </Paper>
            )}

            {feedbackQuestionnaireEmpty && (
              <Alert
                color="orange"
                variant="light"
                icon={<IconAlertCircle size={18} />}
                title={t('perfReview.emptyFeedbackQuestionnaireTitle')}
              >
                {t('perfReview.emptyFeedbackQuestionnaireBody')}
              </Alert>
            )}

            {hasAnonymous && (
              <Paper withBorder radius="md" p="md">
                <Flex gap="md" align="flex-start">
                  <ThemeIcon size={32} radius="md" color="teal" variant="light">
                    <IconLock size={16} />
                  </ThemeIcon>
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Text size="sm" fw={600} mb={4}>
                      {t('perfReview.anonymousPartTitle')}
                    </Text>
                    <Text size="xs" c="dimmed" lh={1.55}>
                      {t('perfReview.anonymousPartBody')}
                    </Text>
                    {/* Без числа оценщиков: состав оценщику не раскрываем. */}
                    <Text size="xs" c="dimmed" lh={1.55} mt={6}>
                      {t('perfReview.anonymityHint')}
                    </Text>
                  </Box>
                </Flex>
              </Paper>
            )}

            {sections.map((section) => (
              <Stack key={section.key} gap="sm" mt="sm">
                {(sections.length > 1 || section.key !== '__unnamed__') && (
                  <Flex align="center" gap="md">
                    <Text
                      size="xs"
                      fw={600}
                      c="dimmed"
                      style={{ letterSpacing: '0.14em', textTransform: 'uppercase' }}
                    >
                      {section.title}
                    </Text>
                    <Box
                      style={{
                        flex: 1,
                        height: 1,
                        background: 'var(--mantine-color-default-border)',
                      }}
                    />
                  </Flex>
                )}
                {section.questions.map((qq, idx) => {
                  const answered = isAnswered(qq, answers[qq.id ?? '']);
                  const num = String(idx + 1);
                  // Comment made required by a scored rating in the same block.
                  const commentRequired = commentRequiredIds.has(qq.id ?? '');
                  return (
                    <Paper
                      key={qq.id}
                      withBorder
                      radius="lg"
                      p="lg"
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '28px 1fr',
                        gap: 14,
                        alignItems: 'flex-start',
                      }}
                    >
                      {answered ? (
                        <ThemeIcon size={22} radius="xl" color="teal" variant="filled" mt={2}>
                          <IconCheck size={12} />
                        </ThemeIcon>
                      ) : (
                        <ThemeIcon
                          size={22}
                          radius="xl"
                          color="gray"
                          variant="default"
                          mt={2}
                          style={{ fontSize: 11, fontWeight: 600 }}
                        >
                          {num}
                        </ThemeIcon>
                      )}
                      <Box style={{ minWidth: 0 }}>
                        <QuestionField
                          question={
                            {
                              ...qq,
                              required: qq.required || commentRequired,
                            } as Parameters<typeof QuestionField>[0]['question']
                          }
                          value={
                            answers[qq.id ?? ''] as string | number | string[] | null | undefined
                          }
                          onChange={(v) => setAnswers((s) => ({ ...s, [qq.id ?? '']: v }))}
                          readOnly={readOnly}
                          deanonymized={!!deanonMap[qq.id ?? '']}
                          onDeanonChange={(v) => setDeanonMap((s) => ({ ...s, [qq.id ?? '']: v }))}
                        />
                        {commentRequired && !answered && !readOnly && (
                          <Text size="xs" c="orange" mt={6}>
                            {t('perfReview.commentRequiredForScore')}
                          </Text>
                        )}
                      </Box>
                    </Paper>
                  );
                })}
              </Stack>
            ))}
          </Stack>
        </Box>

        {!readOnly && (
          <Box
            style={{
              flexShrink: 0,
              background: 'var(--mantine-color-body)',
              borderTop: '1px solid var(--mantine-color-default-border)',
              padding: isMobile
                ? '12px 16px calc(env(safe-area-inset-bottom, 0) + 12px)'
                : '14px 28px',
            }}
          >
            <Flex align="center" justify="space-between" gap="md" wrap="wrap">
              <Text size="xs" c="dimmed">
                {feedbackQuestionnaireEmpty ? (
                  <Text component="span" c="orange" fw={500} inherit>
                    {t('perfReview.emptyFeedbackQuestionnaireTitle')}
                  </Text>
                ) : requiredRemaining > 0 ? (
                  <>
                    {t('perfReview.footerNeedToFill', 'Чтобы отправить — заполни')}{' '}
                    <Text component="span" c="orange" fw={700} inherit>
                      {requiredRemaining}
                    </Text>{' '}
                    {t('perfReview.requiredQuestions', { count: requiredRemaining })}
                  </>
                ) : (
                  <Group gap={6} component="span" c="teal">
                    <IconCheck size={12} />
                    <Text component="span" c="teal" fw={500} inherit>
                      {t('perfReview.footerReady', 'Всё готово к отправке')}
                    </Text>
                  </Group>
                )}
              </Text>
              <Group gap="xs" wrap="nowrap">
                <Button variant="default" onClick={handleClose} size={isMobile ? 'sm' : 'md'}>
                  {t('common.close')}
                </Button>
                <Button
                  variant="light"
                  color="gray"
                  loading={saveDraft.isPending}
                  onClick={handleSaveDraft}
                  size={isMobile ? 'sm' : 'md'}
                  leftSection={<IconDeviceFloppy size={14} />}
                >
                  {t('perfReview.saveDraft')}
                </Button>
                <Button
                  color="teal"
                  loading={submit.isPending}
                  onClick={handleSubmit}
                  disabled={feedbackQuestionnaireEmpty || requiredRemaining > 0}
                  size={isMobile ? 'sm' : 'md'}
                  rightSection={<IconArrowRight size={14} />}
                >
                  {t('perfReview.submit')}
                </Button>
              </Group>
            </Flex>
          </Box>
        )}

        {canReopen && (
          <Box
            style={{
              flexShrink: 0,
              background: 'var(--mantine-color-body)',
              borderTop: '1px solid var(--mantine-color-default-border)',
              padding: isMobile
                ? '12px 16px calc(env(safe-area-inset-bottom, 0) + 12px)'
                : '14px 28px',
            }}
          >
            <Flex align="center" justify="space-between" gap="md" wrap="wrap">
              <Text size="xs" c="dimmed">
                {t('perfReview.reopenHint')}
              </Text>
              <Group gap="xs" wrap="nowrap">
                <Button variant="default" onClick={handleClose} size={isMobile ? 'sm' : 'md'}>
                  {t('common.close')}
                </Button>
                <Button
                  variant="light"
                  color="blue"
                  loading={reopen.isPending}
                  onClick={handleReopen}
                  size={isMobile ? 'sm' : 'md'}
                  leftSection={<IconArrowBackUp size={14} />}
                >
                  {t('perfReview.reopen')}
                </Button>
              </Group>
            </Flex>
          </Box>
        )}
      </div>
    </Drawer>
  );
};
